/**
 * Playable iframe 管理器
 * 使用 LRU 策略限制同时可见的 iframe 数量，避免页面卡顿
 *
 * 策略说明：
 * - 预加载：提前加载即将进入可视区域的 iframe
 * - CSS 隐藏：被 LRU 淘汰的 iframe 用 CSS 隐藏而非移除，滚动回来时无需重新加载
 * - 共享 Observer：全局共享一个 IntersectionObserver，避免创建过多实例
 * - 加载节流：控制同时加载的请求数量，避免网络和渲染压力
 */

interface PlayableInstance {
  id: string;
  src: string;
  setVisible: (visible: boolean) => void; // 控制是否显示 iframe（已加载的不会移除，只是隐藏）
  lastVisibleTime: number;
  element: HTMLElement; // 用于观察的 DOM 元素
}

const MAX_ACTIVE_IFRAMES = 30; // 同时最多存在 60 个 iframe（包含预加载的）
const PRELOAD_MARGIN = 400; // 预加载区域：上下各扩展 800px
const MAX_CONCURRENT_LOADS = 3; // 同时最多加载 3 个 iframe
const LOAD_INTERVAL = 100; // 每次加载之间的间隔（毫秒）

class PlayableManager {
  private instances: Map<string, PlayableInstance> = new Map();
  private activeIds: Set<string> = new Set(); // 当前已加载 iframe 的实例
  private observer: IntersectionObserver | null = null;

  // 加载队列相关
  private loadQueue: string[] = []; // 等待加载的实例 ID 队列
  private loadingCount = 0; // 当前正在加载的数量
  private isProcessingQueue = false; // 是否正在处理队列

  /**
   * 通知 manager iframe 加载完成
   */
  onLoadComplete() {
    this.loadingCount = Math.max(0, this.loadingCount - 1);

    // 如果队列中还有等待的，继续处理
    if (this.loadQueue.length > 0) {
      this.processQueue();
    }
  }

  /**
   * 注册实例并开始观察
   */
  register(
    id: string,
    src: string,
    setVisible: (visible: boolean) => void,
    element: HTMLElement,
  ) {
    // 设置 data-playable-id 用于 IntersectionObserver 回调中识别
    element.setAttribute('data-playable-id', id);

    const instance: PlayableInstance = {
      id,
      src,
      setVisible,
      lastVisibleTime: Date.now(),
      element,
    };

    this.instances.set(id, instance);

    // 开始观察该元素
    const observer = this.getObserver();
    observer.observe(element);

    // 检查是否已经在预加载区域内（首次注册时可能已经可见）
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const isInPreloadZone =
      rect.top < windowHeight + PRELOAD_MARGIN && rect.bottom > -PRELOAD_MARGIN;
    if (isInPreloadZone) {
      this.queueLoad(id);
    }
  }

  /**
   * 更新实例的 src（用于 previewImg 变化时）
   */
  updateSrc(id: string, src: string) {
    const instance = this.instances.get(id);
    if (instance) {
      instance.src = src;
      // 重新加入加载队列
      if (!this.activeIds.has(id) && !this.loadQueue.includes(id)) {
        this.loadQueue.push(id);
        this.processQueue();
      }
    }
  }

  /**
   * 取消注册并停止观察
   */
  unregister(id: string) {
    const instance = this.instances.get(id);
    if (instance) {
      // 停止观察
      this.observer?.unobserve(instance.element);
    }
    this.instances.delete(id);
    this.activeIds.delete(id);
    // 从加载队列中移除
    const queueIndex = this.loadQueue.indexOf(id);
    if (queueIndex > -1) {
      this.loadQueue.splice(queueIndex, 1);
    }
  }

  /**
   * 检查实例是否当前已加载
   */
  isLoaded(id: string): boolean {
    return this.activeIds.has(id);
  }

  /**
   * 获取当前已加载数量（用于调试）
   */
  getLoadedCount(): number {
    return this.activeIds.size;
  }

  /**
   * 获取总实例数量（用于调试）
   */
  getTotalCount(): number {
    return this.instances.size;
  }

  /**
   * 获取队列长度（用于调试）
   */
  getQueueLength(): number {
    return this.loadQueue.length;
  }

  /**
   * 隐藏最久未见的实例（LRU 策略）
   * 用 CSS 隐藏而非移除，滚动回来时可以立即显示
   */
  private evictLRU() {
    let oldestId: string | null = null;
    let oldestTime = Infinity;

    // 找到最久未见的活跃实例
    this.activeIds.forEach(id => {
      const instance = this.instances.get(id);
      if (instance && instance.lastVisibleTime < oldestTime) {
        oldestTime = instance.lastVisibleTime;
        oldestId = id;
      }
    });

    // 隐藏而非移除
    if (oldestId) {
      const instance = this.instances.get(oldestId);
      if (instance) {
        instance.setVisible(false);
      }
      this.activeIds.delete(oldestId);
    }
  }

  /**
   * 获取或创建全局共享的 IntersectionObserver
   */
  private getObserver(): IntersectionObserver {
    if (this.observer) return this.observer;

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const id = entry.target.getAttribute('data-playable-id');
          if (!id) return;

          const instance = this.instances.get(id);
          if (!instance) return;

          if (entry.isIntersecting) {
            // 进入预加载区域，加入加载队列
            this.queueLoad(id);
          }
        });
      },
      {
        rootMargin: `${PRELOAD_MARGIN}px 0px`,
        threshold: 0,
      },
    );

    return this.observer;
  }

  /**
   * 将实例加入加载队列
   */
  private queueLoad(id: string) {
    // 如果已经加载或已在队列中，跳过
    if (this.activeIds.has(id) || this.loadQueue.includes(id)) return;

    this.loadQueue.push(id);
    this.processQueue();
  }

  /**
   * 加载指定实例的 iframe
   */
  private loadInstance(id: string) {
    const instance = this.instances.get(id);
    if (!instance) return;

    instance.lastVisibleTime = Date.now();

    // 如果已经加载，只是重新显示
    if (this.activeIds.has(id)) return;

    // 如果超过最大数量，隐藏最久未见的
    if (this.activeIds.size >= MAX_ACTIVE_IFRAMES) {
      this.evictLRU();
    }

    // 显示当前实例
    this.activeIds.add(id);
    this.loadingCount += 1;
    instance.setVisible(true);
  }

  /**
   * 处理加载队列（带节流）
   */
  private processQueue() {
    if (this.isProcessingQueue) return;
    if (this.loadQueue.length === 0) return;
    if (this.loadingCount >= MAX_CONCURRENT_LOADS) return;

    this.isProcessingQueue = true;

    const processNext = () => {
      // 清理队列中已加载或无效的项
      while (
        this.loadQueue.length > 0 &&
        (this.activeIds.has(this.loadQueue[0]) ||
          !this.instances.has(this.loadQueue[0]))
      ) {
        this.loadQueue.shift();
      }

      if (this.loadQueue.length === 0) {
        this.isProcessingQueue = false;
        return;
      }

      if (this.loadingCount >= MAX_CONCURRENT_LOADS) {
        // 等待有加载完成后再继续
        this.isProcessingQueue = false;
        return;
      }

      const id = this.loadQueue.shift();
      if (id) {
        this.loadInstance(id);
      }

      // 如果队列还有项且未达到并发上限，继续处理
      if (
        this.loadQueue.length > 0 &&
        this.loadingCount < MAX_CONCURRENT_LOADS
      ) {
        setTimeout(processNext, LOAD_INTERVAL);
      } else {
        this.isProcessingQueue = false;
      }
    };

    processNext();
  }
}

// 单例
export const playableManager = new PlayableManager();

// 生成唯一 ID
let idCounter = 0;
export const generatePlayableId = () => {
  idCounter += 1;
  return `playable-${idCounter}`;
};
